import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LayoutGrid, List, Plus, GripVertical, Kanban, Table2 } from "lucide-react";
import { toast } from "sonner";
import { SourceSelect } from "@/components/common/SourceSelect";
import { RecruitingTeamPanel } from "@/components/recruiting/RecruitingTeamPanel";

const ALL = "__all__";

export default function Pipeline({ embedded = false, forcedScope = null, initialFilters = null, initialPipeline = null }) {
  const navigate = useNavigate();
  const { user, isManager } = useAuth();
  const scopeRef = useRef(forcedScope || "all");
  const [scope, setScope] = useState(forcedScope || "all");
  const [lookups, setLookups] = useState({ pipelines: [], statuses: [], candTypes: [], users: [], orgs: [], sources: [] });
  const [pid, setPid] = useState(initialPipeline || ALL);
  const [view, setView] = useState("board");
  const [board, setBoard] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [filters, setFilters] = useState({ candidate_type: "", pipeline_id: "", status: "", recruiter_id: "", organization_id: "", source: "", ...(initialFilters || {}) });

  const statusLabel = (k) => lookups.statuses.find((s) => s.key === k)?.label || k;

  const loadBoard = useCallback(async (p) => {
    setLoading(true);
    try {
      const district_org_id = localStorage.getItem("tr_district") || "";
      const sc = scopeRef.current !== "all" ? scopeRef.current : "";
      const { data } = await api.get("/opportunities/board", { params: { pipeline_id: p, district_org_id, scope: sc } });
      setBoard(data);
      const { data: list } = await api.get("/opportunities", { params: { pipeline_id: p, district_org_id, scope: sc, page_size: 500 } });
      setRows(list.opportunities);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  const loadAll = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = { page_size: 500, district_org_id: localStorage.getItem("tr_district") || "" };
      if (scopeRef.current !== "all") params.scope = scopeRef.current;
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/opportunities", { params });
      setRows(data.opportunities);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/pipelines"), api.get("/statuses"), api.get("/candidate-types"),
      api.get("/recruiting-team/recruiters").catch(() => ({ data: { items: [] } })),
      api.get("/organizations").catch(() => ({ data: { organizations: [] } })),
      api.get("/sources"),
    ]).then(([p, s, c, u, o, src]) => {
      const pipelines = p.data.items.filter((x) => x.active !== false);
      setLookups({ pipelines, statuses: s.data.items, candTypes: c.data.items,
        users: u.data.items || [], orgs: o.data.organizations || [], sources: src.data.items });
      if (pid !== ALL) { setView("board"); loadBoard(pid); } else { loadAll(filters); }
    }).catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPipeline = (v) => {
    setPid(v);
    if (v === ALL) { setView("table"); loadAll(filters); }
    else { setView("board"); loadBoard(v); }
  };

  const selectScope = (v) => {
    setScope(v); scopeRef.current = v;
    if (pid === ALL) loadAll(filters); else loadBoard(pid);
  };

  useEffect(() => {
    const h = () => { if (pid === ALL) loadAll(filters); else loadBoard(pid); };
    window.addEventListener("tr-district-change", h);
    return () => window.removeEventListener("tr-district-change", h);
  }, [pid, filters, loadAll, loadBoard]);

  const applyFilters = (nf) => { setFilters(nf); if (pid === ALL) loadAll(nf); };

  const onDrop = async (stageId) => {
    if (!dragId) return;
    const id = dragId; setDragId(null);
    try { await api.post(`/opportunities/${id}/stage`, { stage_id: stageId }); loadBoard(pid); toast.success("Stage updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const isAll = pid === ALL;
  const columns = [
    { key: "person", header: "Person", render: (r) => (
      <div className="flex items-center gap-2"><UserAvatar name={r.person_name} size={30} />
        <div><p className="font-medium text-slate-800">{r.person_name}{r.person_preferred && <span className="text-slate-400 font-normal"> ({r.person_preferred})</span>}</p>
          <p className="text-xs text-slate-400 font-mono">{r.human_id}</p></div></div>) },
    { key: "ctype", header: "Candidate Type", render: (r) => r.candidate_type ? (lookups.candTypes.find((c) => c.key === r.candidate_type)?.label || r.candidate_type) : "—" },
    { key: "pipeline", header: "Pipeline", render: (r) => <span className="text-sm text-slate-700">{r.pipeline_name || "Unknown"}</span> },
    { key: "stage", header: "Current Stage", render: (r) => <span className="text-sm text-slate-700">{r.current_stage_name}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} label={statusLabel(r.status)} /> },
    { key: "recruiter", header: "Recruiter", render: (r) => r.recruiter_name || "—" },
    { key: "org", header: "Organization", render: (r) => r.organization_name || "—" },
    { key: "source", header: "Source", render: (r) => r.source || "—" },
    { key: "last", header: "Last Activity", render: (r) => r.last_activity_at ? new Date(r.last_activity_at).toLocaleDateString() : "—" },
    { key: "priority", header: "Priority", render: (r) => <StatusBadge status={r.priority === "high" || r.priority === "urgent" ? "pending" : "inactive"} label={r.priority} /> },
  ];

  const controls = (
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={pid} onValueChange={selectPipeline}>
        <SelectTrigger className="w-56" data-testid="pipeline-select"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value={ALL} data-testid="pipeline-all"><span className="flex items-center gap-2"><Table2 className="w-4 h-4" /> All Recruiting</span></SelectItem>
          {lookups.pipelines.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`pipeline-opt-${p.id}`}>{p.name}{p.is_default ? " (default)" : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      {!isAll && (
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button onClick={() => setView("board")} data-testid="view-board" className={`px-3 py-2 ${view === "board" ? "bg-brand text-white" : "bg-white text-slate-500"}`}><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => setView("table")} data-testid="view-table" className={`px-3 py-2 ${view === "table" ? "bg-brand text-white" : "bg-white text-slate-500"}`}><List className="w-4 h-4" /></button>
        </div>)}
      <Button className="bg-brand hover:bg-brand-strong" data-testid="add-opportunity-button" onClick={() => setOpenCreate(true)}><Plus className="w-4 h-4 mr-1.5" /> New Opportunity</Button>
    </div>
  );

  return (
    <div>
      {embedded ? (
        <>
          {forcedScope === "team" && <RecruitingTeamPanel orgs={lookups.orgs} />}
          <div className="flex justify-end mb-4">{controls}</div>
        </>
      ) : (
        <>
          <PageHeader title="Recruiting" description="Manage recruiting opportunities across candidate-type pipelines." actions={controls} />
          <div className="flex items-center gap-1 border-b border-slate-200 mb-4 overflow-x-auto tr-scroll" data-testid="recruiting-scope-tabs">
            {[["all", "All Recruiting"], ["mine", "My Candidates"], ...(isManager ? [["team", "My Team"]] : []), ["unassigned", "Unassigned"]].map(([k, l]) => (
              <button key={k} data-testid={`scope-${k}`} onClick={() => selectScope(k)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${scope === k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
            ))}
          </div>
        </>
      )}

      {isAll && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-4" data-testid="all-recruiting-filters">
          <FilterSelect label="Candidate Type" value={filters.candidate_type} onChange={(v) => applyFilters({ ...filters, candidate_type: v })}
            options={[["", "All"], ...lookups.candTypes.map((c) => [c.key, c.label])]} testId="filter-candidate-type" />
          <FilterSelect label="Pipeline" value={filters.pipeline_id} onChange={(v) => applyFilters({ ...filters, pipeline_id: v })}
            options={[["", "All"], ["__none__", "Unknown (no pipeline)"], ...lookups.pipelines.map((p) => [p.id, p.name])]} testId="filter-pipeline" />
          <FilterSelect label="Status" value={filters.status} onChange={(v) => applyFilters({ ...filters, status: v })}
            options={[["", "All"], ...lookups.statuses.map((s) => [s.key, s.label])]} testId="filter-status" />
          <FilterSelect label="Recruiter" value={filters.recruiter_id} onChange={(v) => applyFilters({ ...filters, recruiter_id: v })}
            options={[["", "All"], ...lookups.users.map((u) => [u.id, lookups.users.filter((x) => x.name === u.name).length > 1 && u.email ? `${u.name} (${u.email})` : u.name])]} testId="filter-recruiter" />
          <FilterSelect label="Organization" value={filters.organization_id} onChange={(v) => applyFilters({ ...filters, organization_id: v })}
            options={[["", "All"], ...lookups.orgs.map((o) => [o.id, o.name])]} testId="filter-org" />
          <FilterSelect label="Source" value={filters.source} onChange={(v) => applyFilters({ ...filters, source: v })}
            options={[["", "All"], ...[...new Set(lookups.sources.map((s) => s.label))].map((l) => [l, l])]} testId="filter-source" />
        </div>
      )}

      {loading ? <PageLoader /> : (isAll || view === "table") ? (
        <DataTable columns={isAll ? columns : columns.filter((c) => c.key !== "pipeline")} rows={rows}
          onRowClick={(r) => navigate(`/recruiting/opportunity/${r.id}`)} testId="opportunities-table" resizeKey="recruiting-list-columns"
          empty={<EmptyState icon={Kanban} title="No opportunities" description="Create a recruiting opportunity to get started." />} />
      ) : (
        <div className="flex gap-4 overflow-x-auto tr-scroll pb-4" data-testid="kanban-board">
          {board?.stages?.map((s) => {
            const items = board.by_stage[s.id] || [];
            return (
              <div key={s.id} className="w-72 shrink-0" onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(s.id)} data-testid={`stage-col-${s.id}`}>
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                    <span className="text-sm font-semibold text-slate-700">{s.name}</span></div>
                  <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2">{items.length}</span>
                </div>
                <div className="space-y-2 min-h-[120px] rounded-lg bg-slate-50/60 p-2">
                  {items.map((o) => (
                    <div key={o.id} draggable onDragStart={() => setDragId(o.id)} data-testid={`opp-card-${o.id}`}
                      onClick={() => navigate(`/recruiting/opportunity/${o.id}`)}
                      className="group bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md hover:border-brand/40 cursor-pointer transition-all">
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 text-sm truncate">{o.person_name}{o.person_preferred && <span className="text-slate-400 font-normal"> ({o.person_preferred})</span>}</p>
                          <p className="text-xs text-slate-400 truncate">{(lookups.candTypes.find((c) => c.key === o.candidate_type)?.label) || o.candidate_type || "—"} · {o.organization_name || "—"}</p>
                          <div className="flex items-center justify-between mt-2">
                            <StatusBadge status={o.status} label={statusLabel(o.status)} />
                            {o.recruiter_name && <UserAvatar name={o.recruiter_name} size={22} />}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <p className="text-xs text-slate-300 text-center py-6">Drop here</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openCreate && <CreateOpportunity pipelines={lookups.pipelines} candTypes={lookups.candTypes} sources={lookups.sources}
        defaultPid={isAll ? "" : pid} onClose={() => setOpenCreate(false)}
        onCreated={(o) => { setOpenCreate(false); navigate(`/recruiting/opportunity/${o.id}`); }} />}
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold text-slate-500">{label}</Label>
      <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger className="mt-1 h-9" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white max-h-72">
          {options.map(([v, l]) => <SelectItem key={v || "__all__"} value={v || "__all__"}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function CreateOpportunity({ pipelines, candTypes, sources, defaultPid, onClose, onCreated }) {
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ person_id: "", candidate_type: "", pipeline_id: defaultPid || "", source: "", source_id: "", priority: "normal" });
  const personOrg = people.find((p) => p.id === form.person_id)?.organization_id || "";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/persons", { params: { page_size: 300 } }).then((p) => setPeople(p.data.persons)); }, []);

  // auto-suggest pipeline from candidate type default mapping
  const onCandType = (v) => {
    const ct = candTypes.find((c) => c.key === v);
    setForm((f) => ({ ...f, candidate_type: v, pipeline_id: f.pipeline_id || ct?.default_pipeline_id || "" }));
  };

  const submit = async () => {
    if (!form.person_id) return setError("Select a person");
    setError(""); setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.pipeline_id) delete payload.pipeline_id;
      const { data } = await api.post("/opportunities", payload); toast.success("Opportunity created"); onCreated(data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="create-opportunity-dialog">
        <DialogHeader><DialogTitle>New Recruiting Opportunity</DialogTitle>
          <DialogDescription>Candidate Type determines the default pipeline, which you can override.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <Field label="Person *">
            <Select value={form.person_id} onValueChange={(v) => setForm({ ...form, person_id: v })}>
              <SelectTrigger data-testid="opp-person"><SelectValue placeholder="Select a person" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.human_id})</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Candidate Type">
              <Select value={form.candidate_type} onValueChange={onCandType}>
                <SelectTrigger data-testid="opp-candidate-type"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white">{candTypes.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Pipeline">
              <Select value={form.pipeline_id} onValueChange={(v) => setForm({ ...form, pipeline_id: v })}>
                <SelectTrigger data-testid="opp-pipeline"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent className="bg-white">{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source">
              <SourceSelect organizationId={personOrg} value={form.source_id} label={form.source} testId="opp-source"
                onChange={({ source_id, source }) => setForm({ ...form, source_id, source })}
                onUnavailable={() => setForm((f) => ({ ...f, source_id: "", source: "" }))} />
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="opp-submit" className="bg-brand hover:bg-brand-strong">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</label>{children}</div>;
}
